import {
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  makeStyles
} from '@material-ui/core'
import React from 'react'

import {
  STAGE_LABEL as SHARED_STAGE_LABEL,
  STAGE_ORDER,
  labelsFor
} from './requestStage'

// The lifecycle popup for one request — opened from the Approval/tracking cell
// on the daily sheet. Renders the request's dated hops from the append-only
// event log (request.timeline), request-level and per-item.
// ⭐ The stage names and their order live in requestStage.js, because the
// Status chip on the requests grid now reads the SAME vocabulary (2026-09-01).
// Two copies would let the chip and this popup describe one PO differently.
const STAGE_LABEL = SHARED_STAGE_LABEL
const ORDER = STAGE_ORDER
const NEW = new Set(['received_at_hq', 'dispatched_by_at_apartment'])

// A row HQ typed straight onto the daily sheet has no approval chain — no LXD
// raised it, no cluster manager or At Apartment head signed it off. Showing
// those three stages greyed as "pending" read as if the row were missing
// approvals it never needed (2026-07-22).
const CHAIN_STAGES = ['requested', 'cluster_approved', 'final_approved']
// Stages that only mean something when the line is going to an apartment.
const APARTMENT_STAGES = ['dispatched_by_at_apartment', 'received_at_apartment']
const SHEET_STATUSES = new Set(['in_daily', 'handed_over', 'closed'])
// Same hops, named for a row that was born on the sheet.
const SHEET_LABEL = {
  at_hq: 'On the daily sheet',
  handed_to_at_apartment: 'Handed over'
}

// Every request draws the SAME nine hops, so the tracker reads the same way
// whatever the row is (user 2026-07-23). The ones a row can never reach are
// marked "not applicable" rather than dropped — hiding them made two rows look
// like different trackers, and leaving them on "pending" read as if approvals
// were outstanding.
const stagesFor = (request, byStage) => {
  // Name the receiver: the At Apartment HQ team for a store order, else the
  // specific apartment it was sent to (2026-07-28). Shared with the grid's
  // Status chip via labelsFor.
  const receiverLabel = labelsFor(request)
  const bornOnSheet =
    request.origin === 'daily_sheet' ||
    // Rows that predate `origin`: on the sheet, but no approval ever happened.
    (SHEET_STATUSES.has(request.status) &&
      !CHAIN_STAGES.some((s) => byStage[s]))
  if (!bornOnSheet) {
    return { order: ORDER, label: { ...STAGE_LABEL, ...receiverLabel }, na: new Set() }
  }
  return {
    order: ORDER,
    label: { ...STAGE_LABEL, ...SHEET_LABEL, ...receiverLabel },
    // No LXD raised it and nobody signed it off; and with no apartment there is
    // nothing for At Apartment to dispatch or an apartment to receive.
    na: new Set(
      request.apartment ? CHAIN_STAGES : [...CHAIN_STAGES, ...APARTMENT_STAGES]
    )
  }
}

const fmt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  return (
    d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ', ' +
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  )
}

// Kit rollup (2026-07-25, P3) — Sounak wants the whole kit at a glance, not
// item by item. A "gap" line is one the user flagged: not yet delivered, marked
// unavailable, or the apartment reported not-received / discrepancy. Each gap
// line carries WHERE it is stuck so he can see the kind of gap in one go.
const STUCK_LABEL = {
  pending: 'Not sent to vendor',
  ordered: 'Sent to vendor — awaiting',
  dispatched: 'Received by HQ procurement team — not delivered'
}
const issueFor = (line) => {
  const f = line.fulfilment_status || 'pending'
  const r = line.receipt_status || 'pending'
  if (f === 'unavailable') return { label: 'Unavailable', tone: 'bad' }
  if (r === 'not_received') return { label: 'Not received at apartment', tone: 'bad' }
  if (r === 'discrepancy') return { label: 'Discrepancy (short / damaged / wrong)', tone: 'warn' }
  if (f !== 'delivered') return { label: STUCK_LABEL[f] || 'Not yet delivered', tone: 'warn' }
  return null
}
const rollup = (lines) => {
  const total = lines.length
  let delivered = 0
  let unavailable = 0
  let notDelivered = 0
  let notReceived = 0
  let discrepancy = 0
  const problems = []
  lines.forEach((ln) => {
    const f = ln.fulfilment_status || 'pending'
    const r = ln.receipt_status || 'pending'
    if (f === 'delivered') delivered += 1
    if (f === 'unavailable') unavailable += 1
    if (f !== 'delivered' && f !== 'unavailable') notDelivered += 1
    if (r === 'not_received') notReceived += 1
    if (r === 'discrepancy') discrepancy += 1
    const issue = issueFor(ln)
    if (issue) problems.push({ line: ln, issue })
  })
  const hasBad = unavailable > 0 || notReceived > 0 || discrepancy > 0
  const state = hasBad
    ? { key: 'gaps', label: 'Has gaps', tone: 'bad' }
    : notDelivered > 0
      ? { key: 'progress', label: 'In progress', tone: 'warn' }
      : total > 0
        ? { key: 'complete', label: 'Complete', tone: 'good' }
        : { key: 'empty', label: 'No lines', tone: 'muted' }
  return {
    total, delivered, unavailable, notDelivered, notReceived, discrepancy,
    problems, state
  }
}

const TONE = {
  good: { fg: '#2E7D32', bg: '#EAF3EA', bd: '#CBE3CB' },
  warn: { fg: '#B25A00', bg: '#FBEFE0', bd: '#EAD3B0' },
  bad: { fg: '#B3261E', bg: '#FBE9E7', bd: '#E9C6C1' },
  muted: { fg: '#6C6A63', bg: '#F1EFEA', bd: '#DDD8CF' }
}

const useStyles = makeStyles(() => ({
  timeline: { listStyle: 'none', margin: 0, padding: '4px 0' },
  row: { display: 'grid', gridTemplateColumns: '22px 1fr', gap: 12 },
  mark: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  dot: {
    width: 13, height: 13, borderRadius: '50%', marginTop: 4, flex: '0 0 auto',
    background: '#DAD6CE', border: '2px solid #DAD6CE'
  },
  dotDone: {
    width: 13, height: 13, borderRadius: '50%', marginTop: 4, flex: '0 0 auto',
    background: '#E2603F', border: '2px solid #E2603F'
  },
  dotRej: {
    width: 13, height: 13, borderRadius: '50%', marginTop: 4, flex: '0 0 auto',
    background: '#C9463F', border: '2px solid #C9463F'
  },
  line: { width: 2, flex: '1 1 auto', minHeight: 12, background: '#DAD6CE', margin: '3px 0' },
  lineDone: { width: 2, flex: '1 1 auto', minHeight: 12, background: '#E2603F', margin: '3px 0' },
  body: { paddingBottom: 14, minWidth: 0 },
  name: { fontWeight: 650, fontSize: 14, display: 'flex', alignItems: 'center', gap: 7 },
  namePending: { fontWeight: 500, color: '#9C988F' },
  newtag: {
    fontSize: 9, letterSpacing: '.05em', textTransform: 'uppercase', fontWeight: 700,
    color: '#B84427', background: '#FBEDE8', border: '1px solid #E4CFC6', padding: '1px 5px', borderRadius: 4
  },
  // A stage this row can never reach: hollow, so it reads as out of play
  // rather than outstanding.
  dotNa: {
    width: 13, height: 13, borderRadius: '50%', marginTop: 4, flex: '0 0 auto',
    background: '#fff', border: '2px dashed #D6D2CA'
  },
  when: { fontSize: 12.5, color: '#5f5d57', marginTop: 2 },
  item: { fontSize: 12, color: '#6C6A63', marginTop: 2 },
  pending: { fontSize: 12.5, color: '#9C988F', marginTop: 2 },
  // Per-stage item count line (collapsed by default — 2026-07-25, P1).
  count: {
    fontSize: 12.5, color: '#5f5d57', marginTop: 2,
    display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'
  },
  toggle: {
    fontSize: 12, color: '#B84427', cursor: 'pointer', fontWeight: 600,
    userSelect: 'none'
  },
  // Kit summary card (P3).
  summary: {
    border: '1px solid #E4DFD6', borderRadius: 8, padding: '12px 14px',
    marginBottom: 14, background: '#FAF8F4'
  },
  summaryTop: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  stateChip: {
    fontSize: 12.5, fontWeight: 700, padding: '3px 10px', borderRadius: 999,
    border: '1px solid'
  },
  progressLabel: { fontSize: 13.5, color: '#4A483F', fontWeight: 600 },
  bar: {
    height: 8, borderRadius: 999, background: '#EAE6DE', marginTop: 10,
    overflow: 'hidden'
  },
  barFill: { height: '100%', background: '#4CAF50', borderRadius: 999 },
  badges: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
  badge: {
    fontSize: 11.5, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
    border: '1px solid'
  },
  problemBtn: { marginTop: 10 },
  problemList: { listStyle: 'none', margin: '8px 0 0', padding: 0 },
  problemRow: {
    display: 'flex', justifyContent: 'space-between', gap: 10,
    padding: '6px 0', borderTop: '1px solid #EDE8DF', fontSize: 13
  },
  problemName: { color: '#3A382F', minWidth: 0, wordBreak: 'break-word' },
  problemIssue: { fontWeight: 600, flex: '0 0 auto', fontSize: 12.5 }
}))

const StateChip = ({ state, cls }) => {
  const t = TONE[state.tone] || TONE.muted
  return (
    <span
      className={cls}
      style={{ color: t.fg, background: t.bg, borderColor: t.bd }}>
      {state.label}
    </span>
  )
}

const RequestTracking = ({ request, onClose }) => {
  const c = useStyles()
  const [expanded, setExpanded] = React.useState(() => new Set())
  const [showProblems, setShowProblems] = React.useState(false)
  if (!request) return null
  const events = request.timeline || []
  const byStage = {}
  events.forEach((e) => {
    ;(byStage[e.stage] = byStage[e.stage] || []).push(e)
  })
  const rejected = !!byStage.rejected
  const { order, label, na } = stagesFor(request, byStage)
  // Progress front: the furthest stage drawn that has any event.
  let front = -1
  order.forEach((k, i) => { if (byStage[k]) front = i })

  const lines = request.lines || []
  const kit = rollup(lines)
  const pct = kit.total ? Math.round((kit.delivered / kit.total) * 100) : 0
  const toggleStage = (k) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Tracking — {request.apartment_name || 'HQ direct'}
        {request.po_number && request.po_number !== '—' ? ` · ${request.po_number}` : ''}
      </DialogTitle>
      <DialogContent>
        {request.title && (
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
            {request.title}
          </Typography>
        )}

        {/* Kit summary — the whole order at a glance (P3). */}
        {kit.total > 0 && (
          <div className={c.summary}>
            <div className={c.summaryTop}>
              <StateChip state={kit.state} cls={c.stateChip} />
              <span className={c.progressLabel}>
                {kit.delivered} of {kit.total} delivered
              </span>
            </div>
            <div className={c.bar}>
              <div className={c.barFill} style={{ width: `${pct}%` }} />
            </div>
            {(kit.notDelivered || kit.unavailable || kit.notReceived || kit.discrepancy) > 0 && (
              <div className={c.badges}>
                {kit.notDelivered > 0 && (
                  <span className={c.badge} style={{ color: TONE.warn.fg, background: TONE.warn.bg, borderColor: TONE.warn.bd }}>
                    {kit.notDelivered} in progress
                  </span>
                )}
                {kit.unavailable > 0 && (
                  <span className={c.badge} style={{ color: TONE.bad.fg, background: TONE.bad.bg, borderColor: TONE.bad.bd }}>
                    {kit.unavailable} unavailable
                  </span>
                )}
                {kit.discrepancy > 0 && (
                  <span className={c.badge} style={{ color: TONE.warn.fg, background: TONE.warn.bg, borderColor: TONE.warn.bd }}>
                    {kit.discrepancy} discrepancy
                  </span>
                )}
                {kit.notReceived > 0 && (
                  <span className={c.badge} style={{ color: TONE.bad.fg, background: TONE.bad.bg, borderColor: TONE.bad.bd }}>
                    {kit.notReceived} not received
                  </span>
                )}
              </div>
            )}
            {kit.problems.length > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  className={c.problemBtn}
                  onClick={() => setShowProblems((s) => !s)}>
                  {showProblems ? 'Hide' : `Show ${kit.problems.length} item${kit.problems.length > 1 ? 's' : ''} with gaps`}
                </Button>
                <Collapse in={showProblems}>
                  <ul className={c.problemList}>
                    {kit.problems.map(({ line, issue }, i) => (
                      <li key={line.uuid || i} className={c.problemRow}>
                        <span className={c.problemName}>
                          {line.item_name || 'item'}
                          {line.qty ? ` ×${line.qty}` : ''}
                        </span>
                        <span
                          className={c.problemIssue}
                          style={{ color: (TONE[issue.tone] || TONE.muted).fg }}>
                          {issue.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Collapse>
              </>
            )}
          </div>
        )}

        <ol className={c.timeline}>
          {order.map((k, i) => {
            const evs = byStage[k] || []
            const reqEv = evs.find((e) => !e.line)
            const itemEvs = evs.filter((e) => e.line)
            const done = evs.length > 0
            // A hop this row can never reach — drawn, but plainly out of play.
            const skipped = na.has(k) && !done
            const isLast = i === order.length - 1
            const isOpen = expanded.has(k)
            return (
              <li key={k} className={c.row}>
                <div className={c.mark}>
                  <span
                    className={
                      // eslint-disable-next-line no-nested-ternary
                      done ? c.dotDone : skipped ? c.dotNa : c.dot
                    }
                  />
                  {!isLast && <span className={i < front ? c.lineDone : c.line} />}
                </div>
                <div className={c.body}>
                  <div className={`${c.name} ${done ? '' : c.namePending}`}>
                    {label[k]}
                    {NEW.has(k) && !skipped && <span className={c.newtag}>New</span>}
                  </div>
                  {reqEv && (
                    <div className={c.when}>
                      <b>{fmt(reqEv.at)}</b>{reqEv.by ? ` · ${reqEv.by}` : ''}
                    </div>
                  )}
                  {/* Per-item events collapsed to a count — the full list ran to
                      100+ rows on a bulk kit (P1). Expand on demand. */}
                  {itemEvs.length > 0 && (
                    <>
                      <div className={c.count}>
                        <span>
                          {itemEvs.length} item{itemEvs.length > 1 ? 's' : ''}
                          {reqEv ? '' : <b> · {fmt(itemEvs[itemEvs.length - 1].at)}</b>}
                        </span>
                        <span className={c.toggle} onClick={() => toggleStage(k)}>
                          {isOpen ? 'hide items' : 'show items'}
                        </span>
                      </div>
                      <Collapse in={isOpen}>
                        {itemEvs.map((e, j) => (
                          <div key={j} className={c.item}>
                            {e.item || 'item'}: <b>{fmt(e.at)}</b>
                          </div>
                        ))}
                      </Collapse>
                    </>
                  )}
                  {!done && (
                    <div className={c.pending}>
                      {skipped ? 'not applicable' : 'pending'}
                    </div>
                  )}
                </div>
              </li>
            )
          })}
        </ol>
        {rejected && request.rejection_reason && (
          <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
            Rejected: {request.rejection_reason}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default RequestTracking
